<script setup lang="ts">
/**
 * UiProgress - Design System Progress Bar Component
 *
 * A simple progress indicator for showing completion status.
 *
 * @example
 * <UiProgress :value="75" />
 * <UiProgress :value="50" show-label />
 * <UiProgress indeterminate />
 */
import { computed } from 'vue';

export interface UiProgressProps {
   /** Progress value (0-100) */
   value?: number;
   /** Maximum value */
   max?: number;
   /** Show percentage label */
   showLabel?: boolean;
   /** Indeterminate state (animated, for unknown progress) */
   indeterminate?: boolean;
}

const props = withDefaults(defineProps<UiProgressProps>(), {
   value: 0,
   max: 100,
   showLabel: false,
   indeterminate: false,
});

const percentage = computed(() => {
   return Math.min(100, Math.max(0, (props.value / props.max) * 100));
});

const barClasses = computed(() => {
   const classes = ['bg-primary', 'transition-all duration-300'];

   if (props.indeterminate) {
      classes.push('animate-indeterminate');
   }

   return classes.join(' ');
});
</script>

<template>
   <div class="flex items-center gap-3">
      <!-- Progress bar container -->
      <div
         class="flex-1 bg-neutral-light rounded-full overflow-hidden h-2"
         role="progressbar"
         :aria-valuenow="indeterminate ? undefined : value"
         :aria-valuemin="0"
         :aria-valuemax="max"
      >
         <div
            :class="barClasses"
            class="h-full rounded-full"
            :style="indeterminate ? {} : { width: `${percentage}%` }"
         />
      </div>

      <!-- Label -->
      <span
         v-if="showLabel && !indeterminate"
         class="text-sm text-neutral-dark font-medium min-w-[3ch]"
      >
         {{ Math.round(percentage) }}%
      </span>
   </div>
</template>

<style scoped>
@keyframes indeterminate {
  0% {
    width: 30%;
    margin-left: -30%;
  }
  100% {
    width: 30%;
    margin-left: 100%;
  }
}

.animate-indeterminate {
  animation: indeterminate 1.5s ease-in-out infinite;
}
</style>
